import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

/* /ai-job-matching
   SEO title: AI Job Matching Platform – Find Jobs That Match Your Skills | PlanB Jobs

   PUBLISHING RULES (from the brief):
   - Matching scores must show explainable factors.
   - No promises of guaranteed hiring, perfect matches or guaranteed interviews.
   - Only operational AI features are shown publicly. Everything in CAPABILITIES
     starts as "coming-soon" and stays hidden until you change it to "live".
   - To preview unreleased items internally, render <AiJobMatching previewMode />. */

const STEPS = [
  { title: 'Build your profile', text: 'Add your skills, experience, education and career preferences.' },
  { title: 'Understand your profile', text: 'The platform analyses the information available in your profile.' },
  { title: 'Match relevant opportunities', text: 'Compare candidate skills and preferences against job requirements.' },
  { title: 'Discover better-fit opportunities', text: 'Present relevant opportunities based on the available matching criteria.' },
];

// status: "live" (shown publicly) | "coming-soon" (internal only)
const CAPABILITIES = [
  { name: 'Resume parsing', status: 'coming-soon' },
  { name: 'Skill extraction', status: 'coming-soon' },
  { name: 'Job description analysis', status: 'coming-soon' },
  { name: 'Candidate-job matching score', status: 'coming-soon' },
  { name: 'Experience relevance', status: 'coming-soon' },
  { name: 'Location compatibility', status: 'coming-soon' },
  { name: 'Skill gap identification', status: 'coming-soon' },
  { name: 'AI job recommendations', status: 'coming-soon' },
  { name: 'Employer candidate ranking', status: 'coming-soon' },
];

// Illustrative only: shows how a score can be explained by its factors.
const FACTORS = [
  { label: 'Skills overlap', note: '4 of 5 required skills found', value: 80 },
  { label: 'Experience relevance', note: '2 years in a similar role', value: 65 },
  { label: 'Location compatibility', note: 'Matches preferred city', value: 100 },
];

const RULES = [
  { title: 'Explainable', text: 'Each score comes with the factors behind it.' },
  { title: 'A guide, not a guarantee', text: 'Matching helps you find relevant jobs. It does not promise interviews or hiring.' },
  { title: 'Based on your information', text: 'Results depend on the details in your profile and the job requirements.' },
];

// Neutral labels for the scrolling strip (what a match can look at)
const LOOKS_AT = ['Skills', 'Experience', 'Education', 'Location', 'Job requirements', 'Career preferences', 'Job title', 'Work mode'];

function useSeo(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    const prev = meta.getAttribute('content');
    meta.setAttribute('content', description);
    return () => {
      document.title = prevTitle;
      if (created) meta.remove(); else if (prev !== null) meta.setAttribute('content', prev);
    };
  }, [title, description]);
}

/* ---------------------------- animation helpers ---------------------------- */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) { setOn(true); return; }
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setOn(true); io.disconnect(); } },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, on];
}

function Reveal({ children, delay = 0, className = '', variant = 'up' }) {
  const [ref, on] = useInView(0.12);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`reveal ${variant !== 'up' ? `reveal-${variant}` : ''} ${on ? 'reveal-in' : ''} ${className}`}
    >
      {children}
    </div>
  );
}

function ScrollProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setP(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <div className="fixed top-0 left-0 h-[3px] z-[100] brand-gradient" style={{ width: `${p}%` }} aria-hidden="true" />;
}

/* number that counts up once it is visible */
function CountUp({ to, duration = 1400, delay = 0, suffix = '' }) {
  const [ref, on] = useInView(0.3);
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setV(to); return undefined; }
    let raf;
    let start;
    const t = setTimeout(() => {
      const tick = (now) => {
        if (start == null) start = now;
        const p = Math.min((now - start) / duration, 1);
        setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);
    return () => { clearTimeout(t); cancelAnimationFrame(raf); };
  }, [on, to, duration, delay]);
  return <span ref={ref} className="tabular-nums">{v}{suffix}</span>;
}

/* factor row: bar fills and number counts up when visible */
function FactorRow({ f, delay = 0 }) {
  const [ref, on] = useInView(0.3);
  return (
    <div ref={ref}>
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <div>
          <p className="font-semibold text-sm">{f.label}</p>
          <p className="text-xs text-gray-500">{f.note}</p>
        </div>
        <span className="text-xs font-bold text-white brand-gradient rounded-lg px-2.5 py-1 min-w-[3rem] text-center">
          <CountUp to={f.value} delay={delay} suffix="%" />
        </span>
      </div>
      <div className="h-2 rounded-full bg-purple-100 overflow-hidden">
        <div
          className="h-full rounded-full brand-gradient transition-[width] duration-[1400ms] ease-out"
          style={{ width: on ? `${f.value}%` : '0%', transitionDelay: `${delay}ms` }}
        />
      </div>
    </div>
  );
}

/* ---------------------------------- page ---------------------------------- */
function AiJobMatching({ previewMode = false }) {
  const [user] = useState(() => {
    const s = localStorage.getItem('user');
    return s ? JSON.parse(s) : null;
  });

  useSeo(
    'AI Job Matching Platform – Find Jobs That Match Your Skills | PlanB Jobs',
    'Discover how AI-powered job matching helps connect candidates and employers using skills, experience, location and job requirements.'
  );

  const visible = CAPABILITIES.filter((c) => previewMode || c.status === 'live');

  return (
    <div className="matching-root min-h-screen bg-white overflow-x-clip text-[#1E0F3D]">
      <style>{`
        .brand-gradient { background-image: linear-gradient(90deg,#7C3AED,#DB2777); }
        .matching-root h2 { display:inline-block; background-image:linear-gradient(90deg,#7C3AED,#DB2777); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; color:transparent; }
        .matching-root .brand-gradient h2, .matching-root .animated-gradient h2, .matching-root .dark-h2 { background-image:none; -webkit-text-fill-color:#fff; color:#fff; }
        .animated-gradient { background:linear-gradient(90deg,#7C3AED,#DB2777,#7C3AED); background-size:200% 100%; animation:gradient-shift 8s ease infinite; }
        .marquee-mask { -webkit-mask-image:linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent); mask-image:linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent); }

        .reveal { opacity:0; transform:translateY(32px); transition:opacity .7s cubic-bezier(.2,.8,.2,1), transform .7s cubic-bezier(.2,.8,.2,1); }
        .reveal-left { transform:translateX(-56px); }
        .reveal-right { transform:translateX(56px); }
        .reveal-zoom { transform:scale(.92); }
        .reveal-in { opacity:1; transform:none; }

        @keyframes rise { from { opacity:0; transform:translateY(20px);} to { opacity:1; transform:none;} }
        @keyframes float { 0%,100% { transform:translateY(0) scale(1);} 50% { transform:translateY(-24px) scale(1.06);} }
        @keyframes bob { 0%,100% { transform:translateY(0) rotate(2deg);} 50% { transform:translateY(-10px) rotate(-1deg);} }
        @keyframes shimmer { to { background-position:200% center; } }
        @keyframes gradient-shift { 0%,100% { background-position:0% 50%;} 50% { background-position:100% 50%;} }
        @keyframes marquee-ltr { from { transform:translateX(0);} to { transform:translateX(-50%);} }
        @keyframes marquee-rtl { from { transform:translateX(-50%);} to { transform:translateX(0);} }
        @keyframes pulse-ring { 0% { box-shadow:0 0 0 0 rgba(124,58,237,.45);} 100% { box-shadow:0 0 0 16px rgba(124,58,237,0);} }
        @keyframes cta-glow { 0% { box-shadow:0 0 0 0 rgba(255,255,255,.5);} 100% { box-shadow:0 0 0 18px rgba(255,255,255,0);} }
        @keyframes scan { 0% { top:0; opacity:0;} 10% { opacity:1;} 90% { opacity:1;} 100% { top:100%; opacity:0;} }

        .rise { animation:rise .8s cubic-bezier(.2,.8,.2,1) both; }
        .float-slow { animation:float 9s ease-in-out infinite; }
        .float-slower { animation:float 13s ease-in-out infinite reverse; }
        .bob { animation:bob 5s ease-in-out infinite; }
        .shimmer-text { background-size:200% auto; animation:shimmer 5s linear infinite; }
        .marquee-ltr { animation:marquee-ltr 35s linear infinite; }
        .marquee-rtl { animation:marquee-rtl 45s linear infinite; }
        .pulse-ring { animation:pulse-ring 2s ease-out infinite; }
        .cta-glow { animation:cta-glow 2.4s ease-out infinite; }
        .scan-line { position:absolute; left:0; right:0; height:2px; background:linear-gradient(90deg,transparent,#7C3AED,#DB2777,transparent); animation:scan 4s ease-in-out infinite; }

        .shine-btn { position:relative; overflow:hidden; }
        .shine-btn::after { content:''; position:absolute; top:0; left:0; width:30%; height:100%; background:rgba(255,255,255,.3); transform:translateX(-120%) skewX(-20deg); transition:transform .7s ease; pointer-events:none; }
        .shine-btn:hover::after { transform:translateX(420%) skewX(-20deg); }

        @media (prefers-reduced-motion: reduce) {
          .rise,.float-slow,.float-slower,.bob,.shimmer-text,.marquee-ltr,.marquee-rtl,.pulse-ring,.cta-glow,.scan-line,.animated-gradient { animation:none; }
          .reveal { opacity:1; transform:none; transition:none; }
          .shine-btn::after { transition:none; }
        }
      `}</style>

      <ScrollProgress />
      <Navbar user={user} />

      {/* ================================ HERO ================================ */}
      <section className="relative bg-gradient-to-br from-[#FBF5FF] via-white to-[#FDF0F6] pt-14 pb-24 lg:pt-20 lg:pb-28">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute -top-40 -right-24 w-[36rem] h-[36rem] rounded-full bg-[#7C3AED]/15 blur-3xl float-slow" />
          <div className="absolute -bottom-40 -left-24 w-[30rem] h-[30rem] rounded-full bg-[#DB2777]/12 blur-3xl float-slower" />
          <div className="absolute inset-0 opacity-60" style={{ backgroundImage: 'radial-gradient(rgba(124,58,237,0.10) 1px, transparent 1px)', backgroundSize: '26px 26px' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-14 items-center">
          <div>
            <div className="rise inline-flex items-center gap-2 mb-5 bg-white border border-purple-100 rounded-full px-4 py-1.5 text-xs font-semibold text-gray-600 shadow-sm">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-[#DB2777] opacity-75 animate-ping motion-reduce:animate-none" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-[#DB2777]" />
              </span>
              Matching you can understand
            </div>

            <h1 className="mb-6 text-[40px] sm:text-[56px] font-extrabold leading-[1.05] tracking-tight">
              {['Stop Applying Randomly.', 'Start Finding Relevant Jobs.'].map((t, i) => (
                <span
                  key={t}
                  style={{ animationDelay: `${0.08 + i * 0.12}s` }}
                  className="rise shimmer-text block w-fit bg-gradient-to-r from-[#7C3AED] via-[#DB2777] to-[#7C3AED] bg-clip-text text-transparent"
                >
                  {t}
                </span>
              ))}
            </h1>

            <p className="rise max-w-lg text-lg leading-7 text-gray-600" style={{ animationDelay: '.38s' }}>
              Your next job should match more than just your job title. PlanB Jobs aims to make recruitment more intelligent by connecting candidate profiles with relevant job requirements.
            </p>

            <div className="rise flex flex-wrap gap-3 mt-8" style={{ animationDelay: '.5s' }}>
              <Link to="/jobs" className="shine-btn brand-gradient pulse-ring text-white px-8 py-3.5 rounded-xl font-semibold text-sm shadow-[0_10px_30px_-8px_rgba(124,58,237,0.6)] transition hover:-translate-y-0.5">
                Explore Opportunities
              </Link>
              <a href="#how" className="px-8 py-3.5 rounded-xl font-semibold text-sm text-[#7C3AED] bg-white border border-[#7C3AED]/40 hover:bg-[#F5F0FA] hover:-translate-y-0.5 transition">
                See how it works
              </a>
            </div>
            <p className="rise text-sm text-gray-500 mt-4" style={{ animationDelay: '.6s' }}>
              Matching results are a guide and do not guarantee interviews or hiring.
            </p>
          </div>

          {/* explainable match mock */}
          <div className="relative max-w-md w-full rise lg:justify-self-end" style={{ animationDelay: '.25s' }} aria-hidden="true">
            <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-[#7C3AED]/25 to-[#DB2777]/20 blur-3xl" />

            <div className="relative overflow-hidden bg-white rounded-3xl border border-purple-100 shadow-[0_40px_80px_-30px_rgba(124,58,237,0.45)] p-5">
              <span className="scan-line" />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-bold">Product Designer</p>
                  <p className="text-sm text-gray-500">Fintech startup · Bengaluru</p>
                </div>
                <span className="text-xs font-medium text-[#7C3AED] bg-[#F5F0FA] rounded-full px-3 py-1">Example</span>
              </div>

              <div className="mt-5 space-y-4">
                {FACTORS.map((f, i) => (
                  <FactorRow key={f.label} f={f} delay={500 + i * 250} />
                ))}
              </div>

              <p className="mt-5 pt-4 border-t border-purple-100 text-xs text-gray-500">
                Illustration of how a match can be explained. Scores are guidance, not a prediction of hiring outcomes.
              </p>
            </div>

            <div className="bob absolute -bottom-7 -right-2 w-56 brand-gradient text-white rounded-2xl p-4 shadow-xl">
              <p className="text-xs text-white/80">Why this match</p>
              <p className="font-bold text-sm">3 factors explained</p>
            </div>

            <div className="float-slow absolute -top-5 -left-4 bg-white border border-purple-100 rounded-2xl px-3.5 py-2 shadow-lg flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#C7F464] text-[#17142A] flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><path d="M20 6L9 17l-5-5" /></svg>
              </span>
              <span className="text-xs font-bold">Reasons shown</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ SCROLLING STRIP ============================ */}
      <div className="marquee-mask overflow-hidden py-8 space-y-3" aria-label="What a match can look at">
        <div className="marquee-ltr flex w-max">
          {[...LOOKS_AT, ...LOOKS_AT, ...LOOKS_AT, ...LOOKS_AT].map((l, i) => (
            <span key={i} className="shrink-0 mr-3 px-5 py-2 rounded-full bg-white border border-purple-100 text-sm font-semibold text-[#7C3AED] shadow-sm">{l}</span>
          ))}
        </div>
        <div className="marquee-rtl flex w-max">
          {[...LOOKS_AT, ...LOOKS_AT, ...LOOKS_AT, ...LOOKS_AT].map((l, i) => (
            <span key={i} className="shrink-0 mr-3 px-5 py-2 rounded-full bg-[#F5F0FA] text-sm font-semibold text-[#DB2777]">{l}</span>
          ))}
        </div>
      </div>

      {/* ============================== HOW IT WORKS ============================== */}
      <section id="how" className="scroll-mt-24 max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">How AI matching works</h2>
            <p className="text-gray-500">Four steps from your profile to opportunities that fit.</p>
          </div>
        </Reveal>

        <ol className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="hidden lg:block absolute top-9 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-[#7C3AED] to-[#DB2777] opacity-30" aria-hidden="true" />
          {STEPS.map((s, i) => (
            <li key={s.title} className="h-full">
              <Reveal delay={i * 140} className="h-full">
                <div className="group relative h-full text-center rounded-3xl p-7 bg-white border border-purple-100 transition-all duration-300 hover:-translate-y-2 hover:border-[#DB2777]/40 hover:shadow-[0_30px_60px_-25px_rgba(124,58,237,0.5)]">
                  <div className="relative mx-auto w-[4.5rem] h-[4.5rem] rounded-2xl brand-gradient text-white flex items-center justify-center text-2xl font-extrabold shadow-lg shadow-[#7C3AED]/30 transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110">
                    {i + 1}
                  </div>
                  <h3 className="text-lg font-bold mt-5 mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            </li>
          ))}
        </ol>
      </section>

      {/* ================================ EXPLAINABLE ================================ */}
      <section className="px-6 lg:px-8 pb-20 lg:pb-24">
        <Reveal variant="zoom">
          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[2rem] bg-[#17142A] text-white px-8 lg:px-16 py-14 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#7C3AED]/30 blur-3xl float-slow pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#DB2777]/20 blur-3xl float-slower pointer-events-none" aria-hidden="true" />

            <div className="relative">
              <h2 className="dark-h2 text-3xl sm:text-4xl font-bold tracking-tight mb-3">Every match shows its reasons</h2>
              <p className="text-white/70 max-w-md">
                A score only helps if you can see what is behind it. Matching results should show the factors that shaped them, such as skills, experience and location, so you can judge for yourself.
              </p>
            </div>

            <ul className="relative space-y-3.5">
              {RULES.map((r, i) => (
                <li key={r.title}>
                  <Reveal variant="right" delay={i * 150}>
                    <div className="rounded-2xl bg-white/5 border border-white/10 px-5 py-4 transition-all duration-300 hover:border-[#C7F464] hover:-translate-y-0.5">
                      <p className="font-bold mb-1 flex items-center gap-2.5">
                        <span className="w-2 h-2 rounded-full bg-[#C7F464] shrink-0 animate-pulse motion-reduce:animate-none" />{r.title}
                      </p>
                      <p className="text-sm text-white/70">{r.text}</p>
                    </div>
                  </Reveal>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      {/* CAPABILITIES (hidden until at least one is "live", or previewMode) */}
      {visible.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20 lg:pb-24">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">AI capabilities</h2>
            <p className="text-gray-500 max-w-xl mb-10">What the matching system uses and offers.</p>
          </Reveal>
          <ul className="flex flex-wrap gap-2.5">
            {visible.map((c, i) => (
              <li key={c.name}>
                <Reveal delay={(i % 6) * 70}>
                  <span className="inline-block text-sm bg-white border border-purple-100 rounded-full px-4 py-2 transition-all duration-300 hover:border-[#7C3AED] hover:-translate-y-0.5 hover:shadow-md">
                    {c.name}
                    {c.status !== 'live' && <em className="not-italic text-xs text-gray-400 ml-2.5">Coming soon · internal</em>}
                  </span>
                </Reveal>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ================================== CTA ================================== */}
      <section className="px-6 lg:px-8 pb-20 lg:pb-24">
        <Reveal variant="zoom">
          <div className="max-w-7xl mx-auto animated-gradient rounded-[2rem] relative overflow-hidden px-8 lg:px-16 py-14 text-center shadow-[0_40px_80px_-30px_rgba(124,58,237,0.6)]">
            <div className="absolute inset-0 opacity-15 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '22px 22px' }} aria-hidden="true" />
            <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-white/20 blur-3xl float-slow pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-white/15 blur-3xl float-slower pointer-events-none" aria-hidden="true" />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Find opportunities that fit your profile</h2>
              <p className="text-white/85 mb-8">Create a profile and see jobs that match your skills, experience and preferences.</p>
              <Link to="/jobs" className="cta-glow inline-block bg-white text-[#7C3AED] px-9 py-4 rounded-xl font-bold transition hover:-translate-y-1 hover:shadow-xl">
                Explore Opportunities
              </Link>
              <p className="text-xs text-white/70 mt-6">Matching results are a guide and do not guarantee interviews or hiring.</p>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}

export default AiJobMatching;